import React, { useState } from 'react';
import _capitalize from "lodash/capitalize";
import _map from 'lodash/map';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip
} from "recharts";

import s from './SRLStackedRowGraph.module.css';

const MIN_HEIGHT_TOOLTIP = 78;
const WIDTH_TOOLTIP = 258;
const TRIANGLE_TOOLTIP_HEIGHT = 6;

const getCustomBar = (key) => (props) => {
  const  { fill, height, width, x, y } = props;
  return (
    <path
      fill={fill}
      width={width}
      height={height}
      x={x}
      y={y}
      radius={0}
      d={`M ${x},${y} h ${width} v ${height} h -${width} Z`}
      stroke="white"
      strokeWidth="2"
      data-id={key}
    />
  );
};


const SRLStackedRowGraph = ({ height, chartData, yAxisWidth, legendComponent, bars, tooltipContent = null }) => {
  const fontSizeConfig = {
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fill: '#242424',
  };

  const [activePayload, setActivePayload] = useState(null);

  const onMouseEnter = (props, index, event) => {
    const { answer, x, y, background } = props;
    const { target } = event;
    const key = target.getAttribute('data-id');
    let offsetX = 0;
    const fullWidth = background.x + background.width;
    const halfWidth =  WIDTH_TOOLTIP / 2;
    if (fullWidth < x + halfWidth) {
      offsetX = Math.abs(fullWidth - (x + halfWidth));
    }

    const payload = {
      answer,
      key,
      value: _get(props, `payload.originalData.${key}`),
      targetProperties: {
        x,
        y,
        background,
      },
      offsetX
    };

    // prevent additional repaints
    if (!_isEqual(activePayload, payload)) {
      setActivePayload(payload);
    }
  };

  const getTooltipPosition = (cActivePayload, cYAxisWidth) => {
    const x = _get(cActivePayload, 'targetProperties.x', 0);
    const y = _get(cActivePayload, 'targetProperties.y', 0);
    const offsetX = _get(cActivePayload, 'offsetX', 0);

    return {
      x: x - (cYAxisWidth / 2) - offsetX,
      y: y - MIN_HEIGHT_TOOLTIP - TRIANGLE_TOOLTIP_HEIGHT,
    };
  };


  const getTooltipContent = (cActivePayload) => (props) => (
    tooltipContent({
      ...props,
      activePayload: cActivePayload
    })
  );

  return (
    <ResponsiveContainer height={height}>
      <BarChart
        data={chartData}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        layout="vertical"
        barGap={16}
      >
        { _map(bars, (color, key) => (
          <Bar
            dataKey={key}
            key={key}
            stackId="a"
            fill={color}
            shape={getCustomBar(key)}
            onMouseEnter={onMouseEnter}
          />
        ))}

        <XAxis
          type="number"
          tick={false}
          axisLine={false}
          domain={[0, 100]}
          label={{
            value: 'total students',
            position: 'insideTop',
            fontSize: 14,
            fontFamily: 'Source Sans Pro',
            fontStyle: 'italic',
            fill: 'var(--prs-c-gray-700)',
            offset: -1
          }}
        />

        <YAxis
          type="category"
          dataKey="answer"
          tickLine={false}
          tickMargin={5}
          width={yAxisWidth}
          tick={fontSizeConfig}
        />

        <Tooltip
          content={getTooltipContent(activePayload)}
          cursor={false}
          position={getTooltipPosition(activePayload, yAxisWidth)}
        />
        <CartesianGrid vertical={false} horizontal={false} />
        <Legend verticalAlign="bottom" content={legendComponent} margin={{ bottom: 150 }}/>
      </BarChart>
    </ResponsiveContainer>
  );
};

SRLStackedRowGraph.propTypes = {
  height: PropTypes.number,
  chartData: PropTypes.array,
  yAxisWidth: PropTypes.number,
  legendComponent: PropTypes.elementType,
  bars: PropTypes.object,
  tooltipContent: PropTypes.elementType,
};

export default SRLStackedRowGraph;


const FrequencyLegend = ({ payload }) => (
  <ul className={s.frequencyLegend}>
    {payload.map(item => (
      <li key={item.value}>
        <div className={s.legendColor} style={{ background: item.color }}/>
        <div>{_capitalize(item.value)}</div>
      </li>
    ))}
  </ul>
);

FrequencyLegend.propTypes =  {
  payload: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    value: PropTypes.string
  }))
};

export { FrequencyLegend };

export const FrequencyBars = {
  never: '#DA0052',
  rarely: '#FFAACA',
  sometimes: '#DDDDDD',
  often: 'var(--prs-c-info-800)',
  always: '#45A5B5',
};

export const TimesBars = {
  never: '#FFAACA',
  '1 time': '#DDDDDD',
  '2 times': 'var(--prs-c-info-800)',
  '3+ times': '#45A5B5',
};

/***
 * object to search the answer even if the answer isn't explicit the text use the number to detect the frequency
 * @type {{[number]: frequencyKey}}
 */
export const FrequencyAliases = {
  1: 'never',
  2: 'rarely',
  3: 'sometimes',
  4: 'often',
  5: 'always',
};

/***
 * @type {{[number]: timeKey}}
 */
 export const TimeAliases = {
  Never: 'never',
  1: '1 time',
  2: '2 times',
  3: '3+ times',
};
